import {
  Checkbox,
  CircularProgress,
  IconButton,
  Tooltip,
} from '@material-ui/core';
import ClearIcon from '@material-ui/icons/Clear';
import LaunchRoundedIcon from '@material-ui/icons/LaunchRounded';
import type { ContractTransaction } from 'ethers';
import { ethers } from 'ethers';
import type { FC } from 'react';
import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { useNetwork } from 'wagmi';

import { validEmailRegex } from '../../../../constants/regexs';
import { useCloudfrontNftAsset } from '../../../../hooks/cloudfront-hook';
import { useMarketplaceContract } from '../../../../hooks/marketplace-contract.hooks';
import { useGetNft } from '../../../../hooks/nft-api.hook';
import { useStarsContract } from '../../../../hooks/stars-contract.hooks';
import {
  getIsEthListing,
  getIsStarsListing,
  getListingEthPrice,
  getListingStarsPrice,
} from '../../../../lib/graph/graph-utils';
import type { Listing } from '../../../../lib/graph/MogulMarketplace';
import { AssetType } from '../../../../lib/graph/MogulMarketplace';
import { translateIpfsToPinata } from '../../../../lib/nfts/nft-catalog.service';
import {
  getNetworkCurrencySymbol,
  weiToEth,
} from '../../../../lib/smart-contract-utils';
import { formatIntField, parseIntField } from '../../../../utils/form.util';
import { Button } from '../../../core/button';
import { InputField } from '../../../core/input-field/InputField';
import { Panel } from '../../panel/Panel.component';
import styles from './PurchaseNFTModal.module.css';
import { WalletETHBalanceCard } from './WalletETHBalanceCard.component';

export interface PurchaseNFTModalProps {
  listing: Listing;
  isWhitelisted: boolean;
  isWhitelistAddress: boolean;
}
export interface PurchaseNFT extends PurchaseNFTModalProps {
  hideModal: () => void;
}

export interface BuyNFTFormValues {
  numToBuy?: number;
}

const PurchaseNft: FC<PurchaseNFT> = ({
  isWhitelisted,
  isWhitelistAddress,
  listing,
  hideModal,
}: PurchaseNFT) => {
  const asset = listing.asset;
  const dbNft = useGetNft(asset.tokenAddress, asset.tokenId.toString());
  const { chain } = useNetwork();

  const { cloudfrontUrl: imageCloudfront } = useCloudfrontNftAsset(
    dbNft?.image,
  );
  const { cloudfrontUrl: animationCloudfront } = useCloudfrontNftAsset(
    dbNft?.animation,
  );
  const image = imageCloudfront ?? translateIpfsToPinata(asset.image ?? '');
  const animation_url =
    animationCloudfront ?? translateIpfsToPinata(asset.animation_url ?? '');

  const marketplaceContract = useMarketplaceContract({
    type: listing.asset.type ?? AssetType.Erc1155,
  });
  const starsContract = useStarsContract();

  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [approvingMarketplace, setApprovingMarketplace] = useState(false);
  const [buyWithStars, setBuyWithStars] = useState(getIsStarsListing(listing));
  const [values, setValues] = useState({ numToBuy: 1 });
  const [showEmailField, setEmailFieldVisibility] = useState(false);
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const [disableEmailField, setDisableEmailField] = useState(true);

  useEffect(() => {
    const setVal = async () => {
      const result = await marketplaceContract.isNotificationsEnabled();
      setNotificationsEnabled(result.email.length > 0);
      marketplaceContract.setNotificationEmail(result.email);
      setDisableEmailField(false);
    };
    void setVal();
  }, []);

  const isStarsListing = useMemo(() => getIsStarsListing(listing), [listing]);
  const isEthListing = useMemo(() => getIsEthListing(listing), [listing]);
  const listingStarsPrice = useMemo(
    () => getListingStarsPrice(listing),
    [listing],
  );
  const listingEthPrice = useMemo(() => getListingEthPrice(listing), [listing]);

  const oneListingPrice = weiToEth(
    buyWithStars ? (listingStarsPrice as string) : (listingEthPrice as string),
  );
  const [purchaseAmount, setPurchaseAmount] = useState(
    (parseFloat(oneListingPrice) * values.numToBuy).toString(),
  );

  const input = <T extends keyof BuyNFTFormValues>(
    field: T,
    parse: (value: string) => string | undefined,
    format: (value: BuyNFTFormValues[T]) => string,
  ) => ({
    onChange: (event: React.ChangeEvent<HTMLInputElement>) => {
      const target = event.currentTarget;

      const value = target.value;
      const parsed = parse(value);

      setValues(v => ({ ...v, [field]: parsed }));

      // update purchase amount
      if (parsed) {
        setPurchaseAmount(
          (parseFloat(oneListingPrice) * parseInt(parsed, 10)).toString(),
        );
      }
    },
    value: format(values[field]),
  });

  const approveMarketplaceMoguls = async (amount: string) => {
    setApprovingMarketplace(true);

    let result = false;
    try {
      result = await starsContract.approve(marketplaceContract.address, amount);
      // result = await starsContract.approveInfinite(marketplaceContract.address);
    } finally {
      setApprovingMarketplace(false);
    }

    return result;
  };

  const [tx, setTx] = useState('');
  const [txPending, setTxPending] = useState(false);
  const explorerUrl = `${chain?.blockExplorers?.default.url}/tx/${tx}`;

  const doesSupportSeveralNFT = asset.type !== 'ERC721';

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = async event => {
    event.preventDefault();

    if (isWhitelisted && !isWhitelistAddress) {
      toast.error('Wallet address is not eligible for whitelist sale');
      return;
    }

    if (submitting || !marketplaceContract.initialized) {
      return;
    }

    setSubmitting(true);
    let txn: ContractTransaction | undefined;
    try {
      const purchaseAmountWei = ethers.utils.parseEther(purchaseAmount);

      let approved = false;
      if (buyWithStars) {
        const allowance = await starsContract.currentWalletAllowanceBN(
          marketplaceContract.address,
        );

        approved =
          purchaseAmountWei.lte(allowance) ||
          (await approveMarketplaceMoguls(purchaseAmountWei.toString()));

        if (!approved) {
          setSubmitting(false);
          return;
        }
      }

      await marketplaceContract
        .buyTokens(
          listing.listingId as string,
          doesSupportSeveralNFT ? values.numToBuy : 1,
          buyWithStars,
        )
        .then(result => {
          txn = result;
        })
        .catch(error => {
          setSubmitting(false);
          console.error(error);
        });
      if (txn) {
        setTx(txn.hash);
        setTxPending(true);
        await txn.wait();
        setTxPending(false);
      }
    } catch (e) {
      setSubmitting(false);
      console.error(e);
      // throw e;
    }

    setSubmitting(false);
  };

  const price = parseFloat(
    weiToEth(
      buyWithStars
        ? (listingStarsPrice as string)
        : (listingEthPrice as string),
    ),
  );

  const balance = parseFloat(
    buyWithStars
      ? marketplaceContract.walletDetails.balance.stars.formatted
      : marketplaceContract.walletDetails.balance.native.formatted,
  );

  return (
    <Panel
      className="overflow-hidden"
      bodyClassName={styles.panel + ' flex flex-col rounded-lg'}
    >
      <div className="flex flex-row">
        {tx === '' ? (
          <div className={styles.purchaseNFTInfo + ' flex flex-col'}>
            <div className="flex">
              <div className={'flex flex-col items-center pl-6 pt-6'}>
                {animation_url ? (
                  <video
                    className={styles.image + ' md:w-96 w-56 mx-6'}
                    src={animation_url}
                    autoPlay
                    loop
                    muted
                  />
                ) : (
                  image && (
                    <img
                      src={image}
                      alt="nft"
                      className={styles.image + ' object-contain md:w-96 w-56'}
                    />
                  )
                )}
                <div className={styles.title + ' text-center py-4'}>
                  {asset.title}
                </div>
                <p className={styles.sellerEthAddress + ' text-center pb-6'}>
                  Created by{' '}
                  {(asset.creator?.address as string).slice(0, 9) +
                    '...' +
                    (asset.creator?.address as string).slice(-6)}
                </p>

                <form
                  autoComplete="off"
                  onSubmit={handleSubmit}
                  className="mb-4 w-full"
                >
                  <p className={styles.inputLabel}>Purchase with:</p>
                  <select
                    name="Token Type"
                    id="tokenType"
                    className={styles.selectPriceType}
                    onBlur={event =>
                      setBuyWithStars(event.target.value === 'stars')
                    }
                    onChange={event =>
                      setBuyWithStars(event.target.value === 'stars')
                    }
                  >
                    {isStarsListing && <option value="stars">MOGUL</option>}
                    {isEthListing && (
                      <option value="eth">{getNetworkCurrencySymbol()}</option>
                    )}
                  </select>
                  {doesSupportSeveralNFT && (
                    <>
                      <p className={`${styles.inputLabel} pt-6`}>
                        Number of Items to purchase
                      </p>
                      <InputField
                        hideLabel
                        label=""
                        InputProps={{
                          disabled: submitting,
                          className: styles.inputField,
                          ...input('numToBuy', parseIntField, formatIntField),
                        }}
                      />
                    </>
                  )}

                  {notificationsEnabled ? (
                    <p>
                      <Checkbox
                        checked
                        disabled
                        style={{
                          color: '#fcd559',
                        }}
                      />
                      Already Subscribed to Email Notifications
                    </p>
                  ) : (
                    <>
                      <p>
                        <Tooltip
                          title="To be notified about future marketplace transactions, you will be asked to sign a message on metamask before confirming your current Digital Collectible Purchase. The signed message is only used to verify your wallet address"
                          placement="top-start"
                          arrow
                        >
                          <Checkbox
                            disabled={disableEmailField}
                            onChange={() =>
                              setEmailFieldVisibility(!showEmailField)
                            }
                            style={{
                              color: '#fcd559',
                            }}
                          />
                        </Tooltip>
                        Subscribe to Email Notifications
                      </p>
                      {showEmailField ? (
                        <InputField
                          label=""
                          className="text-white"
                          InputProps={{
                            onChange: (
                              e: React.ChangeEvent<HTMLInputElement>,
                            ) => {
                              setEmail(e.target.value);
                              marketplaceContract.setNotificationEmail(
                                e.target.value,
                              );
                            },
                          }}
                        />
                      ) : null}
                    </>
                  )}
                  <Tooltip
                    disableHoverListener={balance >= price}
                    title="Insufficient balance"
                    placement="top-start"
                    arrow
                  >
                    <span>
                      <Button
                        style={
                          !marketplaceContract.initialized ||
                          submitting ||
                          balance < price
                            ? { pointerEvents: 'none' }
                            : {}
                        }
                        type="submit"
                        fullWidth
                        disabled={
                          !marketplaceContract.initialized ||
                          submitting ||
                          balance < price ||
                          listing.quantity < values.numToBuy ||
                          // eslint-disable-next-line eqeqeq
                          values.numToBuy == 0 ||
                          (showEmailField && !validEmailRegex.test(email)) ||
                          (isWhitelisted ? !isWhitelistAddress : false)
                        }
                      >
                        {!submitting && !approvingMarketplace ? (
                          'Purchase • ' +
                          purchaseAmount +
                          (buyWithStars
                            ? ' MOGUL'
                            : ` ${getNetworkCurrencySymbol()}`)
                        ) : (
                          <>
                            {approvingMarketplace ? 'Approving' : 'Purchasing'}
                            <CircularProgress
                              className={'placeholder-white::placeholder ml-4'}
                              size={20}
                            />
                          </>
                        )}
                      </Button>
                    </span>
                  </Tooltip>
                  {balance < price && (
                    <p
                      className={styles.royalDetails}
                      style={{ color: '#ffda5d' }}
                    >
                      Insufficient balance
                    </p>
                  )}
                  <p className={styles.royalDetails}>
                    The creator of this collection will receive{' '}
                    {Math.floor(
                      (asset.commission?.commissionBasisPoint ?? 0) / 100,
                    )}
                    % of the sale total from future sales of this item.
                  </p>
                </form>
                <div className="mt-auto mb-4 w-full">
                  <WalletETHBalanceCard
                    walletBalance={balance.toString()}
                    isStars={buyWithStars}
                  />
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className={`${styles.confirmation}`}>
            {txPending ? (
              <div>
                <div className={`${styles.confirmationTitle}`}>
                  Please wait for the transaction to finish.
                </div>
                <div className={`${styles.confirmationProgress}`}>
                  <CircularProgress
                    style={{ color: '#fcd559' }}
                    className={'placeholder-white::placeholder ml-4'}
                    size={40}
                  />
                </div>
                <div>Transaction Hash:</div>
                <a
                  className={`${styles.confirmationLink}`}
                  href={explorerUrl}
                  target="_blank"
                  rel="noreferrer"
                  title="View on explorer"
                >
                  {tx.substring(0, 8) + '...' + tx.substr(tx.length - 4)}
                  <LaunchRoundedIcon
                    className={`${styles.linkIcon}`}
                    fontSize="small"
                  />
                </a>
              </div>
            ) : (
              <div>
                <div className={`${styles.confirmationTitle}`}>
                  Transaction Completed!
                </div>
                <div>Transaction Hash:</div>
                <a
                  className={`${styles.confirmationLink}`}
                  href={explorerUrl}
                  target="_blank"
                  rel="noreferrer"
                  title="View on explorer"
                >
                  {tx.substring(0, 8) + '...' + tx.substr(tx.length - 4)}
                  <LaunchRoundedIcon
                    className={`${styles.linkIcon}`}
                    fontSize="small"
                  />
                </a>
              </div>
            )}
          </div>
        )}

        <div className="ml-4 relative">
          <IconButton
            className={'h-10 w-10 top-2 right-2 z-30'}
            style={{
              background: '#000000',
              border: 'none',
              position: 'absolute',
            }}
            onClick={hideModal}
          >
            <ClearIcon style={{ color: 'white' }} />
          </IconButton>
        </div>
      </div>
    </Panel>
  );
};
export const PurchaseNFTModal =
  (props: PurchaseNFTModalProps) =>
  ({ hideModal }: PurchaseNFT) => {
    return <PurchaseNft hideModal={hideModal} {...props} />;
  };
